import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
	ArrowLeft,
	CircleCheck,
	CircleDot,
	Layers,
	RotateCcw,
	UserMinus,
	UserRound
} from "lucide-react"
import { api } from "../api/client"
import { useApp } from "../state/AppState"
import { accent } from "../design/palette"
import { ProgressChart } from "../components/charts/ProgressChart"
import { SplitBar, StatTile } from "../components/charts/StatTile"
import {
	Avatar,
	Card,
	EmptyState,
	SectionHeading,
	Select,
	Spinner,
	cx
} from "../components/ui"
import { relativeTime } from "../lib/format"

const WINDOWS = [
	{ value: 14, label: "Last 14 days" },
	{ value: 30, label: "Last 30 days" },
	{ value: 90, label: "Last 90 days" }
]

export function InsightsPage() {
	const { projectId } = useParams()
	const { toast, projects } = useApp()
	const [days, setDays] = useState(30)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)

	const project = projects.find((p) => p.project_id === projectId)

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		api
			.projectDashboard(projectId, days)
			.then((result) => {
				if (!cancelled) setData(result)
			})
			.catch((error) => toast(error.message, "error"))
			.finally(() => {
				if (!cancelled) setLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [projectId, days, toast])

	if (loading && !data) return <Spinner label="Loading insights" />
	if (!data) return null

	const { counters } = data

	return (
		<div className="space-y-5">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<Link
						to={`/projects/${projectId}`}
						className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900"
					>
						<ArrowLeft size={14} aria-hidden />
						{project?.name || "Back to issues"}
					</Link>
					<h1 className="mt-2 text-xl font-semibold tracking-tight text-ink-900">
						Insights
					</h1>
					<p className="mt-0.5 text-[0.8125rem] text-ink-500">
						Whether this conversation is converging, and where it is stuck.
					</p>
				</div>
				<Select
					value={days}
					onChange={(event) => setDays(Number(event.target.value))}
					className="w-40"
					aria-label="Chart window"
				>
					{WINDOWS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>

			<div className="grid grid-cols-3 gap-3">
				<StatTile label="Open" value={counters.open} tone="open" icon={CircleDot} />
				<StatTile
					label="Resolved"
					value={counters.closed}
					tone="closed"
					icon={CircleCheck}
					hint={`${counters.resolution_rate}% resolution rate`}
				/>
				<StatTile label="Total raised" value={counters.total} icon={Layers} />
			</div>

			<Card className="p-4 sm:p-5">
				<ProgressChart series={data.series} title="Progress over time" />
			</Card>

			<div className="grid gap-6 lg:grid-cols-5">
				<div className="lg:col-span-3">
					<SectionHeading
						title="By tag"
						description="Where the outstanding questions are concentrated."
						className="mb-3"
					/>
					{data.by_tag.length === 0 ? (
						<EmptyState
							icon={Layers}
							title="Nothing tagged yet"
							description="Tag a few issues and the breakdown appears here."
						/>
					) : (
						<Card className="divide-y divide-ink-100">
							{data.by_tag.map((row) => {
								const tone = accent(row.colour)
								return (
									<div
										key={row.tag_id || "untagged"}
										className="flex items-center gap-3 px-3.5 py-2.5"
									>
										<span
											className={cx("h-2 w-2 shrink-0 rounded-full", tone.dot)}
											aria-hidden
										/>
										<span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-ink-800">
											{row.name}
										</span>
										<span className="w-36 shrink-0">
											<SplitBar open={row.open} closed={row.closed} />
										</span>
										<span className="w-24 shrink-0 text-right text-2xs tabular text-ink-500">
											{row.open} open / {row.total}
										</span>
									</div>
								)
							})}
						</Card>
					)}
				</div>

				<div className="lg:col-span-2">
					<SectionHeading
						title="Recent movement"
						description="The last dozen status changes."
						className="mb-3"
					/>
					<ActivityList activity={data.activity} projectId={projectId} />
				</div>
			</div>
		</div>
	)
}

// Every event kind the feed can receive needs an entry here. Falling back to
// "raised" made an assignment look like a second, duplicate creation.
const COPY = {
	opened: { verb: "raised", icon: CircleDot, className: "text-state-open" },
	closed: { verb: "resolved", icon: CircleCheck, className: "text-state-closed" },
	reopened: { verb: "reopened", icon: RotateCcw, className: "text-state-open" },
	assigned: { verb: "assigned", icon: UserRound, className: "text-state-open" },
	unassigned: { verb: "unassigned", icon: UserMinus, className: "text-ink-400" }
}

function ActivityList({ activity, projectId }) {
	const { peopleById, timezone } = useApp()

	if (!activity?.length) {
		return (
			<EmptyState
				icon={CircleDot}
				title="Nothing has moved yet"
				description="Raising or resolving an issue shows up here."
			/>
		)
	}

	return (
		// Six rows then scroll, matching the dashboard.
		<Card className="max-h-[22.5rem] divide-y divide-ink-100 overflow-y-auto scrollbar-slim">
			{activity.map((event) => {
				const copy = COPY[event.kind] || COPY.opened
				const Icon = copy.icon
				const person = peopleById[event.actor_id]
				return (
					<Link
						key={event.event_id}
						to={`/projects/${projectId}/issues/${event.issue_id}`}
						className="flex items-start gap-2.5 px-3.5 py-2.5 transition-colors first:rounded-t-card last:rounded-b-card hover:bg-ink-50"
					>
						<Avatar person={person} size="sm" className="mt-0.5" />
						<div className="min-w-0 flex-1">
							<p className="text-[0.8125rem] leading-snug text-ink-700">
								<span className="font-medium text-ink-900">
									{person?.name || "Someone"}
								</span>{" "}
								{copy.verb}{" "}
								{event.subject_id && (
									<span className="font-medium text-ink-900">
										{peopleById[event.subject_id]?.name || "someone"} to{" "}
									</span>
								)}
								<span className="font-medium">#{event.issue_number}</span>{" "}
								{event.issue_title}
							</p>
							<p className="mt-0.5 flex items-center gap-1 text-2xs text-ink-400">
								<Icon size={11} className={copy.className} aria-hidden />
								{relativeTime(event.at, timezone)}
							</p>
						</div>
					</Link>
				)
			})}
		</Card>
	)
}
